import { useEffect, useMemo, useRef, useState } from 'react';
import * as _ from 'lodash';
import {
  browseFiles,
  listFileFolders,
} from '../../redux/actions/table/files';

const FILES_PER_PAGE = 25;
const UNALLOCATED = 'unallocated';

const initialFilters = {
  active_folder_id: null,
  folder_filter: null,
  query: '',
  show_archived: false,
  page: 1,
};

const folderIds = (folder) => [
  folder.id,
  ..._.flatMap(folder.children || [], folderIds),
];

const useFilesBrowser = (stage_id, linked_file_ids = []) => {
  const [folders, setFolders] = useState([]);
  const [files, setFiles] = useState([]);
  const [expandedFolderIds, setExpandedFolderIds] = useState([]);
  const [selectedFileIds, setSelectedFileIds] = useState([]);
  const [selectedFilesById, setSelectedFilesById] = useState({});
  const [searchValue, setSearchValue] = useState('');
  const [filters, setFilters] = useState(initialFilters);
  const [pages, setPages] = useState(1);
  const [allFilesTotal, setAllFilesTotal] = useState(0);
  const [unallocatedCount, setUnallocatedCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const filesRequestId = useRef(0);
  const searchTimeout = useRef(null);

  const linkedFileIds = useMemo(
    () => new Set(_.map(linked_file_ids, Number)),
    [linked_file_ids],
  );

  useEffect(() => {
    let active = true;

    listFileFolders(stage_id).then(({ response }) => {
      if (!active) {
        return;
      }

      const nextFolders = _.get(response, 'data.folders', []);

      setFolders(nextFolders);
      setExpandedFolderIds(_.map(nextFolders, 'id'));
      setUnallocatedCount(
        _.get(response, 'data.unallocated_count', 0),
      );
    });

    return () => {
      active = false;
    };
  }, [stage_id]);

  useEffect(() => {
    const requestId = ++filesRequestId.current;
    let active = true;

    setLoading(true);

    browseFiles(
      stage_id,
      _.pickBy(
        {
          pagination: 1,
          page: filters.page,
          limit: FILES_PER_PAGE,
          query: _.trim(filters.query) || undefined,
          filter_folder: filters.folder_filter || undefined,
          show_archived: filters.show_archived ? 1 : 0,
          sort_by: 'file_name',
          order: 'asc',
        },
        (value) => !_.isUndefined(value),
      ),
    )
      .then(({ response }) => {
        if (!active || requestId !== filesRequestId.current) {
          return;
        }

        const total = _.get(response, 'pagination.total', 0);
        const isUnfiltered =
          _.isNil(filters.active_folder_id) &&
          !_.trim(filters.query) &&
          !filters.show_archived;

        setFiles(_.get(response, 'data', []));
        setPages(Math.max(_.get(response, 'pagination.pages', 1), 1));
        setLoading(false);

        if (isUnfiltered) {
          setAllFilesTotal(total);
        }
      })
      .catch(() => {
        if (active && requestId === filesRequestId.current) {
          setLoading(false);
        }
      });

    return () => {
      active = false;
    };
  }, [
    stage_id,
    filters.page,
    filters.query,
    filters.show_archived,
    filters.folder_filter,
    filters.active_folder_id,
  ]);

  useEffect(
    () => () => {
      clearTimeout(searchTimeout.current);
    },
    [],
  );

  const selectAllFiles = () => {
    setFilters((current) => ({
      ...current,
      active_folder_id: null,
      folder_filter: null,
      page: 1,
    }));
  };

  const selectFolder = (folder) => {
    setFilters((current) => ({
      ...current,
      active_folder_id: folder.id,
      folder_filter: _.join(folderIds(folder), '|'),
      page: 1,
    }));
  };

  const selectUnallocated = () => {
    setFilters((current) => ({
      ...current,
      active_folder_id: UNALLOCATED,
      folder_filter: UNALLOCATED,
      page: 1,
    }));
  };

  const toggleFolder = (folderId) => {
    setExpandedFolderIds((current) =>
      _.includes(current, folderId)
        ? _.without(current, folderId)
        : [...current, folderId],
    );
  };

  const search = (query) => {
    setSearchValue(query);
    clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => {
      setFilters((current) => ({ ...current, query, page: 1 }));
    }, 300);
  };

  const toggleArchived = () => {
    setFilters((current) => ({
      ...current,
      show_archived: !current.show_archived,
      page: 1,
    }));
  };

  const isAlreadyLinked = (fileId) => linkedFileIds.has(Number(fileId));

  const toggleFile = (fileId) => {
    const normalizedId = Number(fileId);

    if (isAlreadyLinked(normalizedId)) {
      return;
    }

    setSelectedFileIds((current) => {
      if (_.includes(current, normalizedId)) {
        setSelectedFilesById((currentFiles) =>
          _.omit(currentFiles, [normalizedId]),
        );

        return _.without(current, normalizedId);
      }

      const file = _.find(files, (row) => Number(row.id) === normalizedId);

      if (file) {
        setSelectedFilesById((currentFiles) => ({
          ...currentFiles,
          [normalizedId]: file,
        }));
      }

      return [...current, normalizedId];
    });
  };

  const selectablePageFiles = useMemo(
    () =>
      _.reject(files, (file) => linkedFileIds.has(Number(file.id))),
    [files, linkedFileIds],
  );

  const selectablePageFileIds = useMemo(
    () => _.map(selectablePageFiles, (file) => Number(file.id)),
    [selectablePageFiles],
  );

  const selectedFiles = useMemo(
    () =>
      _.compact(
        _.map(selectedFileIds, (fileId) => selectedFilesById[fileId]),
      ),
    [selectedFileIds, selectedFilesById],
  );

  const allPageSelected =
    !_.isEmpty(selectablePageFileIds) &&
    _.every(selectablePageFileIds, (fileId) =>
      _.includes(selectedFileIds, fileId),
    );

  const togglePageFiles = () => {
    setSelectedFileIds((current) => {
      if (allPageSelected) {
        setSelectedFilesById((currentFiles) =>
          _.omit(currentFiles, selectablePageFileIds),
        );

        return _.difference(current, selectablePageFileIds);
      }

      setSelectedFilesById((currentFiles) => ({
        ...currentFiles,
        ..._.keyBy(selectablePageFiles, (file) => Number(file.id)),
      }));

      return _.union(current, selectablePageFileIds);
    });
  };

  const setPage = (page) => {
    setFilters((current) => ({ ...current, page }));
  };

  return {
    folders,
    files,
    expandedFolderIds,
    selectedFileIds,
    selectedFiles,
    searchValue,
    activeFolderId: filters.active_folder_id,
    showArchived: filters.show_archived,
    page: filters.page,
    pages,
    allFilesTotal,
    unallocatedCount,
    loading,
    selectablePageFileIds,
    allPageSelected,
    selectAllFiles,
    selectFolder,
    selectUnallocated,
    toggleFolder,
    search,
    toggleArchived,
    isAlreadyLinked,
    toggleFile,
    togglePageFiles,
    setPage,
  };
};

export default useFilesBrowser;
